import type { TFunction } from 'i18next';
import type { CalendarItem } from './calendar-item';
import type { BadgeVariant } from '../../components/symphony/ui/Badge';

// The words an item carries beyond its title, shared by the grid, the agenda and the details.

export function itemTitle(item: CalendarItem, t: TFunction): string {
  if (item.title) return item.title;
  return t(item.kind === 'meeting' ? 'calendar.item.untitledMeeting' : 'calendar.item.untitled');
}

/** One line under the title: who and where for a booking, the account for a meeting. */
export function itemMeta(item: CalendarItem, t: TFunction, spaceName?: string): string {
  const parts: string[] = [];
  if (item.kind === 'booking') {
    if (item.capacity) parts.push(t('calendar.item.places', { booked: item.capacity.booked, total: item.capacity.total }));
    else if (item.people?.[0]?.name) parts.push(item.people[0].name);
    if (item.staff?.length) parts.push(item.staff.join(', '));
    if (item.sourceLabel) parts.push(item.sourceLabel);
  } else if (item.kind === 'meeting') {
    if (item.location) parts.push(item.location);
    const guests = item.people?.length ?? 0;
    if (guests > 1) parts.push(t('calendar.item.guests', { count: guests }));
    if (item.sourceLabel) parts.push(item.sourceLabel);
  } else {
    if (spaceName) parts.push(spaceName);
    if (item.kind === 'routine') parts.push(t(item.repeatsDaily ? 'calendar.item.everyDay' : 'calendar.item.routineRun'));
  }
  return parts.join(' · ');
}

/** The status words an item shows, each in the Badge style the rest of the app gives that state. */
export function itemTags(item: CalendarItem, t: TFunction): { label: string; variant: BadgeVariant }[] {
  const tags: { label: string; variant: BadgeVariant }[] = [];
  if (item.needsYou) {
    tags.push({ label: t(item.kind === 'booking' ? 'calendar.status.pendingApproval' : 'calendar.status.needsYou'), variant: 'waiting-on-you' });
  }
  if (item.status === 'cancelled') tags.push({ label: t('calendar.status.cancelled'), variant: 'paused' });
  if (item.status === 'declined') tags.push({ label: t('calendar.status.declined'), variant: 'paused' });
  if (item.kind === 'task' && item.status === 'done') tags.push({ label: t('calendar.status.done'), variant: 'done' });
  if (item.kind === 'task' && item.status === 'failed') tags.push({ label: t('calendar.status.failed'), variant: 'failed' });
  if (item.kind === 'task' && item.status === 'inProgress') tags.push({ label: t('calendar.status.inProgress'), variant: 'in-progress' });
  if (item.kind === 'meeting' && item.rsvp === 'pending') tags.push({ label: t('calendar.rsvp.pending'), variant: 'waiting-on-a-reply' });
  if (item.kind === 'meeting' && item.rsvp === 'tentative') tags.push({ label: t('calendar.rsvp.tentative'), variant: 'waiting-on-a-reply' });
  tags.push(...bookingTags(item, t));
  return tags;
}

const PAYMENT_TAG: Record<string, { key: string; variant: BadgeVariant }> = {
  paid: { key: 'calendar.booking.paid', variant: 'done' },
  not_paid: { key: 'calendar.booking.notPaid', variant: 'waiting-on-you' },
  partially_paid: { key: 'calendar.booking.partlyPaid', variant: 'in-progress' },
  refunded: { key: 'calendar.booking.refunded', variant: 'paused' },
};

const ATTENDANCE_TAG: Record<string, { key: string; variant: BadgeVariant }> = {
  attended: { key: 'calendar.booking.checkedIn', variant: 'done' },
  no_show: { key: 'calendar.booking.noShow', variant: 'failed' },
};

/**
 * One client's payment and attendance as badges; for a class, the count still owing rather than a badge
 * per person. The spec's appointment chips: paid or not, checked in, no-show, waiting list.
 */
function bookingTags(item: CalendarItem, t: TFunction): { label: string; variant: BadgeVariant }[] {
  const clients = (item.booking?.clients ?? []).filter((client) => client.status !== 'canceled' && client.status !== 'declined');
  if (clients.length === 0) return [];
  if (clients.length > 1) {
    const owing = clients.filter((client) => client.payment === 'not_paid' || client.payment === 'partially_paid').length;
    return owing ? [{ label: t('calendar.booking.owingCount', { count: owing }), variant: 'waiting-on-you' }] : [];
  }
  const [client] = clients;
  const tags: { label: string; variant: BadgeVariant }[] = [];
  if (client.status === 'waiting_list') tags.push({ label: t('calendar.booking.waitingList'), variant: 'paused' });
  if (client.payment && PAYMENT_TAG[client.payment])
    tags.push({ label: t(PAYMENT_TAG[client.payment].key), variant: PAYMENT_TAG[client.payment].variant });
  if (client.attendance && ATTENDANCE_TAG[client.attendance]) {
    tags.push({ label: t(ATTENDANCE_TAG[client.attendance].key), variant: ATTENDANCE_TAG[client.attendance].variant });
  }
  return tags;
}
